import type { MatchStatus } from '@fp/core/domain';

const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;

export function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: tz });
}

export function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

export function fmtAgo(iso: string | null | undefined): string {
  if (!iso) return '—';
  const s = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  if (s < 86400) return `${Math.round(s / 3600)} h ago`;
  return `${Math.round(s / 86400)} d ago`;
}

/** ISO → value for <input type="datetime-local"> in the browser's time zone. */
export function toLocalInput(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * ISO → <input type="datetime-local"> value in GMT. Round dates are entered in GMT:
 * a week is whole GMT days, and the next week opens at 00:00 GMT the day after.
 */
export function toGmtInput(iso: string | null | undefined): string {
  return iso ? new Date(iso).toISOString().slice(0, 16) : '';
}

export function fromGmtInput(v: string): string | null {
  if (!v) return null;
  const d = new Date(`${v}:00Z`);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

export function fromLocalInput(v: string): string | null {
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

export const STATUS_LABEL: Record<MatchStatus, string> = {
  SCHEDULED: 'Scheduled',
  FIRST_HALF: 'First half',
  HALF_TIME: 'Half-time',
  SECOND_HALF: 'Second half',
  EXTRA_TIME: 'Extra time',
  PENALTIES: 'Penalties',
  FINISHED: 'Full time',
  SUSPENDED: 'Suspended',
  POSTPONED: 'Postponed',
  ABANDONED: 'Abandoned',
  CANCELLED: 'Void',
  AWARDED: 'Awarded',
};

export const LIVE: MatchStatus[] = ['FIRST_HALF', 'HALF_TIME', 'SECOND_HALF', 'EXTRA_TIME', 'PENALTIES', 'SUSPENDED'];

export function statusPill(status: MatchStatus): string {
  if (LIVE.includes(status) && status !== 'SUSPENDED') return 'pill pill-live';
  if (status === 'FINISHED') return 'pill pill-dark';
  if (status === 'SCHEDULED') return 'pill';
  if (status === 'CANCELLED' || status === 'ABANDONED') return 'pill pill-red';
  return 'pill pill-amber';
}

export function scoreText(m: { rtHome?: number | null; rtAway?: number | null; aetHome?: number | null; aetAway?: number | null }): string {
  if (m.rtHome === null || m.rtHome === undefined) return 'v';
  const aet = m.aetHome !== null && m.aetHome !== undefined ? ` (${m.aetHome}–${m.aetAway} aet)` : '';
  return `${m.rtHome} – ${m.rtAway}${aet}`;
}
